import type { AllegatoCaricato, TipoAllegato } from '@masamune/shared';
import { api, ApiError, API_BASE_URL, refreshAccessToken } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { fraseCaricamento, fraseRete } from './allegatiTesto';

/**
 * Le chiamate degli allegati della chat di MUNE (29/10/2026, fetta 2F «Allegati» della Fase 2;
 * os-planning/ai/fase-2f-allegati.md, «Rotte» e «Il web»). Un file va a POST /ai/allegati appena
 * è pronto, da solo, prima della domanda: POST /ai/chat resta un JSON di 64 KB e porta solo gli
 * id. Qui anche togliere un allegato non ancora mandato e aprire grande la foto di una
 * conversazione riaperta.
 *
 * Il caricamento usa XMLHttpRequest, l'unico modo di avere l'avanzamento di un invio, come
 * components/modals/UploadDocModal.tsx ma senza i suoi tre difetti: l'indirizzo è API_BASE_URL
 * (lib/api.ts), mai ricopiato; al 401 rinnova il token con refreshAccessToken (uno alla volta
 * per tutta l'app, mai una copia: due rinnovi insieme chiudono tutte le sessioni) e riprova una
 * volta; la frase dell'errore ha il nome del file, scelta da `code` e `details` in
 * allegatiTesto.ts, perché quelle del server il nome non l'hanno mai.
 */

/** Senza JSON leggibile: la rete, una pagina d'errore di nginx. */
const SENZA_CODICE = { code: null, message: null, details: undefined } as const;

/** La sessione è scaduta e non si rinnova, come in apiFetch. */
function sessioneScaduta(): never {
  useAuthStore.getState().clear();
  throw new ApiError(401, 'UNAUTHORIZED', 'Sessione scaduta: accedi di nuovo');
}

/** Il corpo d'errore dell'API, { statusCode, code, message, details? } (plugins/error-handler.ts), se c'è. */
function erroreDalCorpo(testo: string): { code: string | null; message: string | null; details?: unknown } {
  let corpo: unknown = null;
  try { corpo = testo ? JSON.parse(testo) : null; } catch { return SENZA_CODICE; }
  if (!corpo || typeof corpo !== 'object') return SENZA_CODICE;
  const c = corpo as { code?: unknown; message?: unknown; details?: unknown };
  return {
    code: typeof c.code === 'string' ? c.code : null,
    message: typeof c.message === 'string' && c.message ? c.message : null,
    details: c.details,
  };
}

function eAllegatoCaricato(x: unknown): x is AllegatoCaricato {
  if (!x || typeof x !== 'object') return false;
  const a = x as Record<string, unknown>;
  return typeof a.id === 'string' && (a.tipo === 'foto' || a.tipo === 'pdf') && typeof a.nome === 'string'
    && (a.pagine === null || typeof a.pagine === 'number');
}

interface PropsCarica {
  /** La foto ridisegnata dal browser, o il PDF com'è. */
  file: Blob;
  /** Il nome che il pezzo ha nella striscia: va nel campo `nome`, il server lo pulisce. */
  nome: string;
  /** La miniatura da 160 px, solo per le foto. */
  miniatura: Blob | null;
  /** La x del pezzo, o il pannello che si smonta: l'invio si interrompe. */
  signal: AbortSignal;
  /** Da 0 a 1, mentre i byte partono. */
  onAvanzamento: (quanto: number) => void;
  /**
   * I byte sono partiti tutti (xhr.upload.onload): da qui la x non interrompe più, aspetta l'id e
   * toglie la riga. Interrompere un invio già arrivato lascerebbe sul server una riga senza id nel
   * browser, che nessuno toglierebbe fino al giro delle 03:00.
   */
  onInviato?: () => void;
  /** Per le frasi: «rovinata» o «rovinato». Senza, dal tipo del file. */
  tipo?: TipoAllegato;
  /** Per le frasi: al telefono il tipo sbagliato nomina anche l'HEIC. */
  mobile?: boolean;
}

/**
 * Carica un file su POST /ai/allegati (multipart: `nome`, `file`, `miniatura`) e restituisce la
 * riga in attesa. Lancia ApiError con la frase da mostrare già scritta, col nome: `status` 0 e
 * `code` 'RETE' quando la rete cade, un DOMException 'AbortError' quando si interrompe.
 */
export async function caricaAllegato(p: PropsCarica): Promise<AllegatoCaricato> {
  const tipo: TipoAllegato = p.tipo ?? (p.file.type === 'application/pdf' ? 'pdf' : 'foto');
  // Il nome vero sta nel campo `nome`: quello del file nel multipart non dice niente.
  const nomeFile = tipo === 'pdf' ? 'allegato.pdf' : p.file.type === 'image/png' ? 'foto.png' : 'foto.jpg';
  const corpo = () => {
    const f = new FormData();
    f.append('nome', p.nome);
    f.append('file', p.file, nomeFile);
    if (p.miniatura) f.append('miniatura', p.miniatura, 'miniatura.jpg');
    return f;
  };

  let r = await invia(corpo(), useAuthStore.getState().accessToken, p);
  if (r.status === 401) {
    // Il 401 nasce prima della rotta (app.authenticate): sul server non è rimasto niente, si riprova.
    const nuovo = await refreshAccessToken();
    if (!nuovo) sessioneScaduta();
    r = await invia(corpo(), nuovo, p);
  }

  if (r.status >= 200 && r.status < 300) {
    let dati: unknown = null;
    try { dati = JSON.parse(r.testo); } catch { dati = null; }
    if (eAllegatoCaricato(dati)) return dati;
    throw new ApiError(502, 'RISPOSTA', fraseRete(p.nome));
  }
  const e = erroreDalCorpo(r.testo);
  const frase = fraseCaricamento({ status: r.status, ...e }, { nome: p.nome, tipo, byte: p.file.size }, !!p.mobile);
  throw new ApiError(r.status, e.code ?? (r.status === 413 ? 'NGINX_413' : 'ERRORE'), frase, e.details);
}

/** Un invio con XMLHttpRequest: lo stato e il testo della risposta, qualunque sia. */
function invia(form: FormData, token: string | null, p: PropsCarica): Promise<{ status: number; testo: string }> {
  return new Promise((ok, no) => {
    if (p.signal.aborted) { no(new DOMException('Caricamento interrotto', 'AbortError')); return; }
    const xhr = new XMLHttpRequest();
    const interrompi = () => xhr.abort();
    const fine = () => p.signal.removeEventListener('abort', interrompi);
    xhr.open('POST', `${API_BASE_URL}/ai/allegati`);
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) p.onAvanzamento(Math.min(1, e.loaded / e.total));
    };
    xhr.upload.onload = () => {
      p.onAvanzamento(1);
      p.onInviato?.();
    };
    xhr.onload = () => { fine(); ok({ status: xhr.status, testo: xhr.responseText }); };
    xhr.onerror = () => {
      fine();
      // La rete che cade: la mascotte in alto apre l'ombrello, come in apiFetch.
      window.dispatchEvent(new Event('msm:rete-giu'));
      no(new ApiError(0, 'RETE', fraseRete(p.nome)));
    };
    xhr.onabort = () => { fine(); no(new DOMException('Caricamento interrotto', 'AbortError')); };
    p.signal.addEventListener('abort', interrompi, { once: true });
    xhr.send(form);
  });
}

/**
 * Toglie un allegato ancora in attesa (la x della striscia): 204, o 404 per le righe già mandate,
 * che vanno via con la conversazione. Chi chiama non aspetta la risposta.
 */
export function togliAllegato(id: string): Promise<void> {
  return api.delete<void>(`/ai/allegati/${encodeURIComponent(id)}`);
}

/**
 * La foto intera di una conversazione riaperta, da GET /ai/allegati/:id: un <img> verso l'API non
 * porta il Bearer, quindi fetch col token, blob e indirizzo locale, come
 * features/projects/ProgettoSezioniMobile.tsx. L'indirizzo lo libera chi chiude la foto.
 */
export async function apriFoto(id: string): Promise<string> {
  const chiedi = (t: string | null) => fetch(`${API_BASE_URL}/ai/allegati/${encodeURIComponent(id)}`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    credentials: 'include',
  });
  let res = await chiedi(useAuthStore.getState().accessToken);
  if (res.status === 401) {
    const nuovo = await refreshAccessToken();
    if (!nuovo) sessioneScaduta();
    res = await chiedi(nuovo);
  }
  if (!res.ok) {
    const e = erroreDalCorpo(await res.text().catch(() => ''));
    throw new ApiError(res.status, e.code ?? 'ERRORE', e.message ?? 'Allegato non trovato', e.details);
  }
  return URL.createObjectURL(await res.blob());
}
